import { Suspense, useState } from 'react'

import { LazyPdfViewer } from '@/modules/projects/components/dialogs/LazyPdfViewer'
import { Fact } from '@/modules/projects/components/Workbench/Fact'
import { FACTS_GRID } from '@/modules/projects/components/Workbench/mapLayout'
import { PageZoomDialog } from '@/modules/projects/components/Workbench/PageZoomDialog'
import {
  PANEL_HEADING,
  PANEL_SECTION,
} from '@/modules/projects/components/Workbench/panelLayout'
import type { DocumentPage } from '@/modules/projects/components/Workbench/types'
import { OpeningPage } from './OpeningPage'

interface PagePreviewProps {
  page: DocumentPage
  statementPages: DocumentPage[]
  filedUnder: string | null
}

// The page itself, read live from its PDF, plus the two facts the app holds about it: which
// sheet of which file it is, and where it is filed. Dates, account numbers and balances belong
// to a statement, never to a page, so this does not claim them.
export const PagePreview = ({
  page,
  statementPages,
  filedUnder,
}: PagePreviewProps) => {
  const [zoomed, setZoomed] = useState(false)
  // Paging inside the dialog moves only the dialog, never the editor's own selection.
  const [zoomedPage, setZoomedPage] = useState(page)
  const openLabel = `Open page ${page.source.pageInFile} larger`

  return (
    <div data-page-reader className={PANEL_SECTION}>
      <p className={PANEL_HEADING}>
        Page {page.source.pageInFile}{' '}
        <span className="font-normal text-ink-subtle">
          ·{' '}
          {page.number === statementPages[0]?.number
            ? 'the first page of the statement'
            : 'the one you clicked'}
        </span>
      </p>
      <button
        type="button"
        title={openLabel}
        aria-label={openLabel}
        onClick={() => {
          setZoomedPage(page)
          setZoomed(true)
        }}
        className="block w-full cursor-zoom-in overflow-hidden rounded-lg border bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Suspense fallback={<OpeningPage />}>
          <LazyPdfViewer
            url={page.source.fileUrl}
            initialPage={page.source.pageInFile}
            pageRange={{
              start: page.source.pageInFile,
              end: page.source.pageInFile,
            }}
            toolbar={false}
            fitPage
          />
        </Suspense>
      </button>
      <PageZoomDialog
        page={zoomedPage}
        statementPages={statementPages}
        open={zoomed}
        onOpenChange={setZoomed}
        onPageChange={setZoomedPage}
      />
      <dl className={FACTS_GRID}>
        <Fact label="File">
          <span
            className="truncate text-ink-subtle"
            title={page.source.fileName}
          >
            {page.source.fileName}
          </span>
        </Fact>
        <Fact label="Filed in">
          {filedUnder ?? <span className="text-ink-subtle">Nowhere yet</span>}
        </Fact>
      </dl>
    </div>
  )
}
